import * as React from "react";
import { useCallback, useRef, useState } from "react";
import { styled, Theme } from "@mui/material";
import { useTranslate } from "../../hooks/useTranslate";
import { useWhatsNextOptions } from "./useWhatsNextOptions";
import {
  WhatsNextOption,
  WhatsNextOptionId,
} from "./WhatsNext/WhatsNextOption";
import Button from "@mui/material/Button";
import clsx from "clsx";
import { usePatientFeatureFlags } from "../../hooks/usePatientFeatureFlags";
import { PatientColorMap } from "../../layout/patientColorMap";
import IconButton from "@mui/material/IconButton";
import { useNativePatientShare } from "../share/useNativePatientShare";
import { isProduction } from "fe-common/utils/envGetter";

//region [[ Styles ]]

const PatientWhatsNextView = styled("div")({
  display: "flex",
  flexFlow: "column",
  justifyContent: "center",
  alignItems: "center",
  padding: "22px",
});

const WhatsNextPageView = styled("div")({
  display: "flex",
  flexFlow: "column",
  alignItems: "center",
  justifyContent: "space-between",
});

const WhatsNextTitle = styled("div")({
  color: PatientColorMap.Default,
  fontSize: "19px",
  lineHeight: "25px",
  fontWeight: 500,
  margin: "22px 0",
});

const WhatsNextOptionContainer = styled("div")({
  display: "flex",
  flexFlow: "row",
  flexWrap: "wrap",
  justifyContent: "space-evenly",
  height: "fit-content",
  padding: "14px",
  boxShadow: "0px 7px 19px rgba(0, 0, 0, 0.14)",
  borderRadius: "8px",
});

const OptionContainer = styled("div")({
  display: "flex",
  flex: "50%",
  flexFlow: "column",
  justifyContent: "flex-start",
  alignItems: "center",
});

const OptionText = styled(Button)({
  "&.option-text, &.option-text:hover,&.option-text:active, &.option-text:focus": {
    background: "transparent",
    textAlign: "center",
    fontSize: "14px",
    lineHeight: "18px",
    fontWeight: 400,
    textTransform: "none",
  },
});

const RoundOption = styled(IconButton)(
  ({ theme, bg }: { theme?: Theme; bg: string }) => ({
    "&.round-option": {
      "&, &:hover, &:active, &:focus": {
        borderRadius: "11px",
        padding: "19px",
        height: "78px",
        width: "78px",
        marginBottom: "9px",
        backgroundColor: bg,
      },
    },
  }),
);
//endregion [[ Styles ]]

//region [[ Props ]]

export interface PatientWhatsNextProps {
  className?: string;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

const EmptyComponent = () => null;

export const PatientWhatsNext = ({ ...props }: PatientWhatsNextProps) => {
  const {
    externalShareIsSupported,
    openExternalShare,
  } = useNativePatientShare();

  const hide = !usePatientFeatureFlags("whatsNext");

  const scrollRef = useRef<HTMLDivElement>(null);

  const [isDialogOpen, setIsDialogOpen] = useState(true);

  const [selectedOption, setSelectedOption] = useState<WhatsNextOption>(null);

  const { translate } = useTranslate();

  const whatsNextOptions = useWhatsNextOptions();

  const createOptionSelectedHandler = useCallback(
    (opt: WhatsNextOption) => () => {
      // Since mobile external share api requires the share window to open only
      // after a user gesture, we can't use any useEffect to load it, and have
      // to make the external share open only by the click handler :(
      // ref: https://w3c.github.io/web-share/#share-method
      if (
        opt.name === WhatsNextOptionId.SendToDoctor &&
        externalShareIsSupported
      ) {
        openExternalShare()
          .then((result) => console.log(result))
          .catch((e) => (!isProduction() ? alert(e) : console.error(e)))
          .finally(() => setIsDialogOpen(false));
      }
      setSelectedOption(opt);
      setIsDialogOpen(true);
    },
    [
      setSelectedOption,
      setIsDialogOpen,
      externalShareIsSupported,
      openExternalShare,
    ],
  );

  const handleDialogClosed = useCallback(() => setIsDialogOpen(false), [
    setIsDialogOpen,
  ]);

  if (hide) return null;

  const ComponentToRender = selectedOption?.component || EmptyComponent;

  return (
    <PatientWhatsNextView
      className={clsx(props.className, "patient-whats-next-view")}
    >
      <WhatsNextPageView className={"whats-next-page-view"} ref={scrollRef}>
        <WhatsNextTitle>{translate("WHAT_TO_DO_NEXT")}</WhatsNextTitle>
        <WhatsNextOptionContainer>
          {whatsNextOptions.map((opt) => (
            <OptionContainer key={opt.name}>
              <RoundOption
                className={"round-option"}
                bg={opt.backgroundColor}
                onClick={createOptionSelectedHandler(opt)}
              >
                <opt.icon />
              </RoundOption>
              <OptionText className={"option-text"}>
                {translate(opt.displayText)}
              </OptionText>
            </OptionContainer>
          ))}
          <ComponentToRender open={isDialogOpen} onClose={handleDialogClosed} />
        </WhatsNextOptionContainer>
      </WhatsNextPageView>
    </PatientWhatsNextView>
  );
};
